import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  UserCircle,
  PanelLeft,
  ChevronDown,
  ShoppingBag,
  Warehouse,
  LogOut,
  Search,
  X,
} from 'lucide-react';
import LanguageSelector from '../LanguageSelector';
import CurrencySelector from '../CurrencySelector';
import { useAuth } from '../../context/AuthContext';
import type { SupportedCurrencyItem } from '../../context/CurrencyContext';
import { LewouhmartLogoMark } from '../branding/LewouhmartLogoMark';
import { FALLBACK_STOREFRONT_THEME } from '../../utils/storefrontTheme';
import type { SidebarGroup, SidebarItem } from './layoutTypes';
import { getSidebarGroupChildren } from './layoutTypes';
import { isWorkspacePath, wsPath } from '../../utils/siteHosts';

type AppHeaderProps = {
  language: string;
  currency: string;
  supportedCurrencies: SupportedCurrencyItem[];
  onLanguageChange: (value: string) => void;
  onCurrencyChange: (value: string) => void;
  /** When false, hides workspace navigation links to avoid duplication with sidebar. */
  showWorkspaceNav?: boolean;
  workspaceSearchItems?: Array<{ label: string; path: string; groupLabel?: string }>;
  hasSidebar?: boolean;
  workspaceNavGroups?: SidebarGroup[];
  activeWorkspaceGroupIndex?: number;
  onWorkspaceGroupSelect?: (index: number) => void;
  workspaceNavBadgeCounts?: Record<string, number>;
};

const BACKOFFICE_LOGO_THEME = {
  brandPrimary: '#334155',
  navAccent: '#0f172a',
};

function AppHeader({
  language,
  currency,
  supportedCurrencies,
  onLanguageChange,
  onCurrencyChange,
  showWorkspaceNav = true,
  workspaceSearchItems = [],
  hasSidebar = false,
  workspaceNavGroups = [],
  activeWorkspaceGroupIndex = 0,
  onWorkspaceGroupSelect,
  workspaceNavBadgeCounts = {},
}: AppHeaderProps) {
  const {
    isAuthenticated,
    auth,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const username = auth.email ? auth.email.split('@')[0] : '';
  const [productsMenuOpen, setProductsMenuOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);
  const [mobileWorkspaceNavOpen, setMobileWorkspaceNavOpen] = React.useState(false);
  const [mobileNavView, setMobileNavView] = React.useState<'groups' | 'items'>('groups');
  const [mobileSelectedGroupIndex, setMobileSelectedGroupIndex] = React.useState(0);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [activeSearchIndex, setActiveSearchIndex] = React.useState(0);
  const headerRef = React.useRef<HTMLElement | null>(null);
  const searchWrapRef = React.useRef<HTMLDivElement | null>(null);
  const desktopSearchInputRef = React.useRef<HTMLInputElement | null>(null);
  const mobileSearchInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const syncHeaderHeight = () => {
      document.documentElement.style.setProperty(
        '--workspace-header-h',
        `${el.offsetHeight}px`,
      );
    };
    syncHeaderHeight();
    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(el);
    window.addEventListener('resize', syncHeaderHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncHeaderHeight);
      document.documentElement.style.removeProperty('--workspace-header-h');
    };
  }, [hasSidebar, showWorkspaceNav, workspaceNavGroups.length]);

  const isAdmin = auth.type === 'ADMIN';
  const permissions = auth.permissions || [];
  const canCatalog = isAdmin || permissions.includes('PRODUCT:READ') || permissions.includes('CATEGORY:READ');
  const canInventory =
    isAdmin
    || permissions.includes('PURCHASE:READ')
    || permissions.includes('STOCK_LOT:READ')
    || permissions.includes('SUPPLIER:READ')
    || permissions.includes('PURCHASE_ORDER:READ')
    || permissions.includes('LOSS:READ');
  const isWorkspace = isWorkspacePath(location.pathname);
  const logoTheme = isWorkspace ? BACKOFFICE_LOGO_THEME : FALLBACK_STOREFRONT_THEME;

  const handleAccountClick = () => {
    if (
      auth.type === 'EMPLOYEE'
      || auth.type === 'ADMIN'
      || auth.type === 'SHIPPING_AGENT'
    ) {
      navigate(wsPath('/workspace/profile'));
    } else {
      navigate('/account');
    }
  };

  const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `relative px-2 py-1 text-xs font-medium tracking-tight transition-colors duration-200 border-b-2 ${
      isActive
        ? 'text-white border-slate-300'
        : 'text-slate-300 border-transparent hover:text-white hover:border-slate-300'
    }`;

  const workspaceGroupNavClasses = (isActive: boolean) =>
    `relative flex min-w-0 flex-1 items-center justify-center px-1 py-1 text-[11px] font-semibold tracking-tight transition-colors duration-200 border-b-2 ${
      isActive
        ? 'text-white border-sky-400 bg-slate-800/90'
        : 'text-slate-300 border-transparent hover:text-white hover:bg-slate-800/60'
    }`;

  const searchResults = React.useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return workspaceSearchItems.slice(0, 8);
    const scored = workspaceSearchItems
      .map((item) => {
        const label = item.label.toLowerCase();
        const group = (item.groupLabel || '').toLowerCase();
        const path = item.path.toLowerCase();
        const labelIdx = label.indexOf(q);
        const groupIdx = group.indexOf(q);
        const pathIdx = path.indexOf(q);
        const score =
          (labelIdx >= 0 ? 0 : 50)
          + (groupIdx >= 0 ? 10 : 30)
          + (pathIdx >= 0 ? 15 : 30)
          + Math.max(labelIdx, 0);
        return { item, hit: labelIdx >= 0 || groupIdx >= 0 || pathIdx >= 0, score };
      })
      .filter((x) => x.hit)
      .sort((a, b) => a.score - b.score)
      .slice(0, 10)
      .map((x) => x.item);
    return scored;
  }, [searchTerm, workspaceSearchItems]);

  const mobileWorkspaceSections = React.useMemo(() => {
    const grouped = new Map<string, Array<{ label: string; path: string }>>();
    workspaceSearchItems.forEach((item) => {
      const key = item.groupLabel || t('backoffice.sidebar_overview', { defaultValue: 'Backoffice' });
      const current = grouped.get(key) || [];
      current.push({ label: item.label, path: item.path });
      grouped.set(key, current);
    });
    return Array.from(grouped.entries()).map(([label, items]) => ({ label, items }));
  }, [t, workspaceSearchItems]);

  React.useEffect(() => {
    if (!searchOpen) return;
    const onDocClick = (ev: MouseEvent) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(ev.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [searchOpen]);

  React.useEffect(() => {
    setActiveSearchIndex(0);
  }, [searchTerm]);

  React.useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (!(ev.metaKey || ev.ctrlKey) || ev.key.toLowerCase() !== 'k') return;
      if (!isAuthenticated || !isWorkspace) return;
      ev.preventDefault();
      if (window.matchMedia('(min-width: 1024px)').matches) {
        setSearchOpen(true);
      } else {
        setMobileSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAuthenticated, isWorkspace]);

  React.useEffect(() => {
    if (!searchOpen) return;
    window.setTimeout(() => {
      desktopSearchInputRef.current?.focus();
      desktopSearchInputRef.current?.select();
    }, 0);
  }, [searchOpen]);

  React.useEffect(() => {
    if (!mobileSearchOpen) return;
    window.setTimeout(() => {
      mobileSearchInputRef.current?.focus();
      mobileSearchInputRef.current?.select();
    }, 0);
  }, [mobileSearchOpen]);

  const goToSearchItem = React.useCallback((path: string) => {
    setSearchOpen(false);
    setSearchTerm('');
    navigate(path);
  }, [navigate]);

  const renderHighlighted = (text: string) => {
    const q = searchTerm.trim();
    if (!q) return text;
    const lowerText = text.toLowerCase();
    const lowerQ = q.toLowerCase();
    const idx = lowerText.indexOf(lowerQ);
    if (idx < 0) return text;
    const pre = text.slice(0, idx);
    const hit = text.slice(idx, idx + q.length);
    const post = text.slice(idx + q.length);
    return (
      <>
        {pre}
        <mark className="rounded-md border border-sky-300/50 bg-sky-400/25 px-1 py-[1px] font-semibold text-sky-50">
          {hit}
        </mark>
        {post}
      </>
    );
  };

  return (
    <header
      ref={headerRef}
      className="fixed left-0 right-0 top-0 z-50 w-full max-w-full overflow-x-clip border-b border-slate-800 bg-slate-900"
    >
      <div className="flex w-full min-w-0 max-w-full items-stretch gap-2 overflow-x-clip px-2 py-1.5 sm:px-3 lg:gap-3 lg:px-4">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="group flex shrink-0 items-center gap-1.5 self-center transition-transform duration-200 hover:scale-[1.02]"
        >
          <LewouhmartLogoMark
            brandPrimary={logoTheme.brandPrimary}
            navAccent={logoTheme.navAccent}
            size={26}
            className="shadow-md shadow-slate-950/30"
          />
          <span className="hidden text-xs font-semibold tracking-tight text-white sm:inline">
            {t('header.brand_name')}
          </span>
        </button>

        {showWorkspaceNav && isAuthenticated && hasSidebar && workspaceNavGroups.length > 0 && (
          <nav
            className="hidden min-w-0 flex-1 items-stretch md:flex"
            role="navigation"
            aria-label={t('header.workspace_navigation_aria', {
              defaultValue: 'Workspace navigation',
            })}
          >
              {workspaceNavGroups.map((group, idx) => {
                const isActive = idx === activeWorkspaceGroupIndex;
                const badgeCount = group.badgeKey
                  ? workspaceNavBadgeCounts[group.badgeKey] || 0
                  : 0;
                return (
                  <button
                    key={`${group.label}:${idx}`}
                    type="button"
                    onClick={() => onWorkspaceGroupSelect?.(idx)}
                    className={workspaceGroupNavClasses(isActive)}
                  >
                    <span className="inline-flex min-w-0 max-w-full items-center justify-center gap-1 truncate">
                      {group.icon && (
                        <span className="flex shrink-0 [&>svg]:h-3 [&>svg]:w-3">
                          {group.icon}
                        </span>
                      )}
                      <span className="truncate">{group.label}</span>
                      {badgeCount > 0 ? (
                        <span className="inline-flex min-w-[1.1rem] items-center justify-center bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
          </nav>
        )}

        {showWorkspaceNav && isAuthenticated && !hasSidebar && (isAdmin || canCatalog || canInventory) && (
          <nav
            className="hidden min-w-0 flex-1 items-stretch justify-between md:flex"
              role="navigation"
              aria-label={t('header.workspace_navigation_aria', {
                defaultValue: 'Workspace navigation',
              })}
            >
              {isAdmin && (
                <>
                  <NavLink to={wsPath('/workspace')} end className={navLinkClasses}>
                    {() => (
                      <span className="inline-flex items-center gap-1.5">
                        <LayoutDashboard className="h-3.5 w-3.5" strokeWidth={2} />
                        {t('backoffice.sidebar_overview')}
                      </span>
                    )}
                  </NavLink>
                  <NavLink to={wsPath('/workspace/employees')} className={navLinkClasses}>
                    {() => (
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" strokeWidth={2} />
                        {t('backoffice.sidebar_employees')}
                      </span>
                    )}
                  </NavLink>
                  <NavLink to={wsPath('/workspace/customers')} className={navLinkClasses}>
                    {() => (
                      <span className="inline-flex items-center gap-1.5">
                        <UserCircle className="h-3.5 w-3.5" strokeWidth={2} />
                        {t('backoffice.sidebar_customers')}
                      </span>
                    )}
                  </NavLink>
                </>
              )}

              {(canCatalog || canInventory) && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setProductsMenuOpen((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium tracking-tight text-sky-100/80 hover:text-white"
                  >
                    <ShoppingBag className="h-3.5 w-3.5" strokeWidth={2} />
                    <span>{t('header.nav_products')}</span>
                    <ChevronDown className="h-3 w-3" strokeWidth={2} />
                  </button>
                  {productsMenuOpen && (
                    <div className="absolute left-0 mt-1 w-44 bg-slate-900/95 py-1 text-[11px] text-sky-50 shadow-lg ring-1 ring-sky-700/70">
                      {canCatalog && (
                        <button
                          type="button"
                          onClick={() => {
                            setProductsMenuOpen(false);
                            navigate(wsPath('/workspace/products'));
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-800"
                        >
                          <ShoppingBag className="h-4 w-4 shrink-0" strokeWidth={2} />
                          <span>{t('header.nav_products_catalog')}</span>
                        </button>
                      )}
                      {canInventory && (
                        <button
                          type="button"
                          onClick={() => {
                            setProductsMenuOpen(false);
                            navigate(wsPath('/workspace/inventory'));
                          }}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-800"
                        >
                          <Warehouse className="h-4 w-4 shrink-0" strokeWidth={2} />
                          <span>{t('header.nav_products_inventory')}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
          </nav>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-1.5 self-center sm:gap-2">
          {isAuthenticated && isWorkspace && (hasSidebar || showWorkspaceNav) ? (
            <button
              type="button"
              onClick={() => setMobileWorkspaceNavOpen((prev) => !prev)}
              className={`inline-flex h-8 items-center justify-center rounded-lg border px-2.5 text-[11px] font-semibold md:hidden ${
                mobileWorkspaceNavOpen
                  ? 'border-sky-300/70 bg-sky-700/70 text-white'
                  : 'border-sky-700/70 bg-sky-950/50 text-sky-100'
              }`}
              aria-label={t('backoffice.sidebar_overview', { defaultValue: 'Backoffice' })}
              aria-expanded={mobileWorkspaceNavOpen}
            >
              <PanelLeft className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
              {mobileWorkspaceNavOpen
                ? t('common.close', { defaultValue: 'Close' })
                : t('common.menu', { defaultValue: 'Menu' })}
            </button>
          ) : null}
          {isAuthenticated && isWorkspace && workspaceSearchItems.length > 0 ? (
            <button
              type="button"
              onClick={() => setMobileSearchOpen(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-sky-700/70 bg-sky-950/50 text-sky-100 lg:hidden"
              aria-label={t('common.search', { defaultValue: 'Search' })}
            >
              <Search className="h-4 w-4" />
            </button>
          ) : null}
          {isAuthenticated && isWorkspace && workspaceSearchItems.length > 0 ? (
            <div ref={searchWrapRef} className="relative hidden lg:block">
              <div className="relative">
                <Search className="pointer-events-none absolute left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-sky-200/70" />
                <input
                  ref={desktopSearchInputRef}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={(e) => {
                    if (!searchOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                      setSearchOpen(true);
                      return;
                    }
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setActiveSearchIndex((p) => Math.min(p + 1, Math.max(searchResults.length - 1, 0)));
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setActiveSearchIndex((p) => Math.max(0, p - 1));
                    } else if (e.key === 'Enter') {
                      const picked = searchResults[activeSearchIndex];
                      if (picked) goToSearchItem(picked.path);
                    } else if (e.key === 'Escape') {
                      setSearchOpen(false);
                    }
                  }}
                  placeholder={t('common.search', { defaultValue: 'Search...' })}
                  className="h-7 w-32 rounded-md border border-sky-700/70 bg-sky-950/50 pl-6 pr-2 text-[11px] text-sky-50 placeholder:text-sky-200/60 focus:border-sky-300 focus:outline-none lg:w-40 xl:w-48 2xl:pr-12"
                />
                <span className="pointer-events-none absolute right-1.5 top-1/2 hidden -translate-y-1/2 rounded border border-sky-700/80 px-1 py-px text-[9px] text-sky-200/80 2xl:inline">
                  {navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl+K'}
                </span>
              </div>
              {searchOpen && (
                <div className="absolute right-0 z-50 mt-1.5 w-[min(34rem,calc(100vw-1rem))] overflow-hidden rounded-xl border border-sky-800/70 bg-slate-950/95 shadow-2xl">
                  <div className="max-h-80 overflow-y-auto p-1.5">
                    {searchResults.length === 0 ? (
                      <div className="rounded-lg px-3 py-2 text-xs text-sky-100/70">
                        {t('common.no_results', { defaultValue: 'No results' })}
                      </div>
                    ) : (
                      searchResults.map((r, idx) => (
                        <button
                          key={`${r.path}:${idx}`}
                          type="button"
                          onClick={() => goToSearchItem(r.path)}
                          className={`w-full rounded-lg px-3 py-2 text-left text-xs transition ${
                            idx === activeSearchIndex ? 'bg-sky-800/60 text-white' : 'text-sky-50 hover:bg-slate-800/80'
                          }`}
                        >
                          <div className="font-semibold">{renderHighlighted(r.label)}</div>
                          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-sky-200/80">
                            {r.groupLabel ? <span>{renderHighlighted(r.groupLabel)}</span> : null}
                            <span className="font-mono text-[10px] text-sky-300/80">{renderHighlighted(r.path)}</span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          <div className="flex items-center gap-1 border-r border-slate-600 pr-1.5">
            <LanguageSelector
              value={language}
              onChange={onLanguageChange}
            />
            <CurrencySelector
              value={currency}
              options={supportedCurrencies}
              onChange={onCurrencyChange}
            />
          </div>
          {isAuthenticated && (
            <div className="flex items-center gap-2">
              {username && (
                <button
                  type="button"
                  onClick={handleAccountClick}
                  className="hidden max-w-[6.5rem] truncate rounded-md bg-sky-800/70 px-2 py-0.5 text-[11px] font-medium text-sky-50 shadow-sm transition-all duration-200 hover:bg-sky-600 focus:outline-none focus:ring-1 focus:ring-sky-200 xl:inline-flex 2xl:max-w-[8rem]"
                >
                  {username}
                </button>
              )}
              <button
                type="button"
                onClick={() => logout()}
                aria-label={t('header.logout_button')}
                className="flex h-6 w-6 items-center justify-center rounded-md border border-red-400/70 bg-red-600/85 text-red-50 shadow-sm transition-all duration-200 hover:bg-red-500 hover:border-red-300 focus:outline-none focus:ring-1 focus:ring-red-300"
              >
                <LogOut className="h-3 w-3" strokeWidth={2} />
              </button>
            </div>
          )}
        </div>
      </div>
      {mobileSearchOpen && isAuthenticated && isWorkspace ? (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-[1px]"
            aria-label={t('header.close_search_aria', {
              defaultValue: 'Close search',
            })}
            onClick={() => setMobileSearchOpen(false)}
          />
          <div className="absolute left-2 right-2 top-14 rounded-xl border border-sky-800/70 bg-slate-950/95 p-2 shadow-2xl">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-sky-200/70" />
              <input
                ref={mobileSearchInputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('common.search', { defaultValue: 'Search workspace...' })}
                className="h-9 w-full rounded-lg border border-sky-700/70 bg-sky-950/50 pl-7 pr-8 text-sm text-sky-50 placeholder:text-sky-200/60 focus:border-sky-300 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setMobileSearchOpen(false)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-sky-200/80"
              >
                {t('common.close', { defaultValue: 'Close' })}
              </button>
            </div>
            <div className="mt-2 max-h-[60vh] overflow-y-auto p-0.5">
              {searchResults.length === 0 ? (
                <div className="rounded-lg px-3 py-2 text-xs text-sky-100/70">
                  {t('common.no_results', { defaultValue: 'No results' })}
                </div>
              ) : (
                searchResults.map((r, idx) => (
                  <button
                    key={`${r.path}:mobile:${idx}`}
                    type="button"
                    onClick={() => {
                      setMobileSearchOpen(false);
                      goToSearchItem(r.path);
                    }}
                    className="w-full rounded-lg px-3 py-2 text-left text-xs text-sky-50 hover:bg-slate-800/80"
                  >
                    <div className="font-semibold">{renderHighlighted(r.label)}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-sky-200/80">
                      {r.groupLabel ? <span>{renderHighlighted(r.groupLabel)}</span> : null}
                      <span className="font-mono text-[10px] text-sky-300/80">{renderHighlighted(r.path)}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
      {mobileWorkspaceNavOpen && isAuthenticated && isWorkspace ? (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-[1px]"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            onClick={() => {
              setMobileWorkspaceNavOpen(false);
              setMobileNavView('groups');
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto border border-sky-800/70 bg-slate-950/95 p-3 shadow-2xl">
            <div className="mb-2 flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                {hasSidebar && mobileNavView === 'items' ? (
                  <button
                    type="button"
                    onClick={() => setMobileNavView('groups')}
                    className="text-xs font-semibold text-sky-200/80 hover:text-white"
                  >
                    {t('common.back', { defaultValue: 'Back' })}
                  </button>
                ) : null}
                <p className="text-xs font-bold uppercase tracking-wide text-sky-100/80">
                  {hasSidebar && mobileNavView === 'items' && workspaceNavGroups[mobileSelectedGroupIndex]
                    ? workspaceNavGroups[mobileSelectedGroupIndex].label
                    : t('backoffice.sidebar_overview', { defaultValue: 'Backoffice' })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMobileWorkspaceNavOpen(false);
                  setMobileNavView('groups');
                }}
                className="inline-flex h-8 w-8 items-center justify-center border border-sky-700/70 text-sky-100"
                aria-label={t('common.close', { defaultValue: 'Close' })}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-1">
              {hasSidebar && workspaceNavGroups.length > 0 ? (
                mobileNavView === 'groups' ? (
                  workspaceNavGroups.map((group, idx) => {
                    const badgeCount = group.badgeKey
                      ? workspaceNavBadgeCounts[group.badgeKey] || 0
                      : 0;
                    return (
                      <button
                        key={`${group.label}:mobile:${idx}`}
                        type="button"
                        onClick={() => {
                          setMobileSelectedGroupIndex(idx);
                          setMobileNavView('items');
                        }}
                        className={`flex w-full items-center gap-2 border-l-4 px-3 py-2.5 text-left text-sm font-medium transition ${
                          idx === activeWorkspaceGroupIndex
                            ? 'border-sky-500 bg-sky-800/70 text-white'
                            : 'border-transparent text-sky-50 hover:bg-slate-800/80'
                        }`}
                      >
                        {group.icon && (
                          <span className="flex shrink-0 [&>svg]:h-4 [&>svg]:w-4">
                            {group.icon}
                          </span>
                        )}
                        <span>{group.label}</span>
                        {badgeCount > 0 ? (
                          <span className="ml-auto inline-flex min-w-[1.1rem] items-center justify-center bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                ) : (() => {
                  const mobileGroup = workspaceNavGroups[mobileSelectedGroupIndex];
                  if (!mobileGroup) return null;
                  const renderMobileItem = (item: SidebarItem) => (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => {
                        setMobileWorkspaceNavOpen(false);
                        setMobileNavView('groups');
                        navigate(item.path);
                      }}
                      className={`flex w-full items-center gap-2 border-l-4 px-3 py-2.5 text-left text-sm font-medium transition ${
                        location.pathname === item.path
                          ? 'border-sky-500 bg-sky-800/70 text-white'
                          : 'border-transparent text-sky-50 hover:bg-slate-800/80'
                      }`}
                    >
                      {item.icon && (
                        <span className="flex shrink-0 [&>svg]:h-4 [&>svg]:w-4">
                          {item.icon}
                        </span>
                      )}
                      {item.label}
                    </button>
                  );
                  if (mobileGroup.sections?.length) {
                    return mobileGroup.sections.map((section, sectionIdx) => (
                      <div key={section.label}>
                        {sectionIdx > 0 ? <div className="my-1 border-t border-sky-800/70" /> : null}
                        <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-sky-200/70">
                          {section.label}
                        </p>
                        <div className="space-y-1">
                          {section.children.map((item) => renderMobileItem(item))}
                        </div>
                      </div>
                    ));
                  }
                  return getSidebarGroupChildren(mobileGroup).map((item) => renderMobileItem(item));
                })()
              ) : mobileWorkspaceSections.length > 0 ? (
                mobileWorkspaceSections.map((section) => (
                  <div key={section.label}>
                    <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-sky-200/70">
                      {section.label}
                    </p>
                    <div className="space-y-1">
                      {section.items.map((item) => (
                        <button
                          key={item.path}
                          type="button"
                          onClick={() => {
                            setMobileWorkspaceNavOpen(false);
                            navigate(item.path);
                          }}
                          className={`flex w-full items-center px-3 py-2 text-left text-sm font-medium transition ${
                            location.pathname === item.path
                              ? 'bg-sky-800/70 text-white'
                              : 'text-sky-50 hover:bg-slate-800/80'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMobileWorkspaceNavOpen(false);
                    navigate(wsPath('/workspace'));
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-sky-50 hover:bg-slate-800/80"
                >
                  <LayoutDashboard className="h-4 w-4 shrink-0" />
                  {t('backoffice.sidebar_overview')}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}

export default AppHeader;


